import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CellContext } from "@tanstack/react-table";
import { Field, Form, Formik } from "formik";
import _ from "lodash";
import { observer } from "mobx-react";
import React, {
  ChangeEvent,
  ChangeEventHandler,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Button } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import {
  DATE_TIME_FORMAT_FILENAME,
  formatNumberWithCommas,
} from "../../constants/constants";
import { ActionType } from "../../constants/enums/action-type-enum";
import addInventoryTypeEnum from "../../constants/enums/add-inventory-type-enum";
import { ColumnType } from "../../constants/enums/grid-column-type-enum";
import { ModuleType } from "../../constants/enums/module-type-enum";
import userTypeEnum from "../../constants/enums/user-type-enum";
import { useStore } from "../../contexts/store-provider";
import { isOperationPermittedRoleBase } from "../../helpers/action-permission-helper";
import {
  getStoreIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../helpers/localstorage-helper";
import { ISorting } from "../../models/ISorting";
import {
  IInventoryList,
  IInventoryVM,
} from "../../models/response/IInventoryManagementResponse";
import FormikFormInput from "../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikMonthYearFieldMUI from "../../shared-components/FormikFormHandlers/FormikMonthYearFieldMUI";
import FormLoader from "../../shared-components/FormLoader/FormLoader";
import Grid, { getColumns } from "../../shared-components/Grid/Grid";
import DropdownInput from "../../shared-components/input/DropdownInput";
import LanguageSlider from "../../shared-components/language-slider/language-slider";
import AddPopupHOC from "../../shared-components/popup/add/add-popup-hoc";
import ShowPopupHOC from "../../shared-components/popup/show/show-popup-hoc";
import { formatMessage } from "../../translations/format-message";
import AddEditProduct from "../master-management/product-list/components/add-edit-product";
import AddInventoryInfo from "./component/add-inventary-info";
import { invertoryFilterValidateSchema } from "../../shared-components/FormikFormHandlers/FormikValidationSchema";
import moment from "moment";
import toast from "react-hot-toast";
import Papa from "papaparse";
import { showAmountWithFormatting } from "../../helpers/common-component-helper";
import "./inventory-management.css";
import { getEffectiveMonthYear } from "../../helpers/date-time-helper";

const InventroyList: React.FC = (props: any) => {
  const { preferencesStore, masterManagementStore, inventoryStore, userStore } =
    useStore();

  const [openPanel, setOpenPanel] = useState(false);

  const {
    getAllInventoryListState,
    GetAllInventoryListService,
    inventoryList,
    allInventoryList,
    addUpdateInventorytState,
    resetAddUpdateInventory,
    resetInventoryList,
    setInventoryFilterDetail,
    resetInventoryFilterDetail,
    inventoryFilter,
  } = inventoryStore;

  const { islanguageChanged, updateLanguageChangedFlag } = preferencesStore;

  const { GetAllStoreOptionService, getAllStoreOptionsForInventory } =
    masterManagementStore;

  const userType = getUserTypeFromLocalStorage();
  let storeId = getStoreIdFromLocalStorage() || 0;

  const [selectedId, setSelectedId] = useState<number>(0);
  const [storeIdSearch, setStoreIdSearch] = useState<number>(
    userType === userTypeEnum[userTypeEnum.Store] ? storeId : -1
  );
  const [selectedRow, setSelectedRow] = useState<IInventoryList | undefined>(
    undefined
  );
  const [selectedAddInventoryType, setSelectedAddInventoryType] =
    useState<addInventoryTypeEnum>(addInventoryTypeEnum.Incoming);
  const [sorting, setSorting] = useState<ISorting>({
    column: "product_name",
    orderBy: "asc",
  });

  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  const handleStoreIdSearch: ChangeEventHandler<HTMLSelectElement> =
    useCallback((event: ChangeEvent<HTMLSelectElement>) => {
      setStoreIdSearch(event.target.value as unknown as number);
    }, []);

  /**
   * This function calls the "getAllGroup" store funtion that get configuration list from API
   */
  const callGetInventoryManagementListService = () => {
    GetAllInventoryListService(
        Number(storeIdSearch),
        sorting.orderBy,
        sorting.column,
        inventoryFilter
      );
  };

  const downloadCSVHandler = () => {
    const columns = [
      "ProductName",
      "UnitPriceExcludingTax",
      "CarryoverQuantity",
      "CarryoverAmount",
      "IncomingQuantity",
      "IncomingAmount",
      "PriceExcludingTax",
      "SalesQuantity",
      "SalesAmount",
      "CostAmount",
      "InternalUsedQuantity",
      "InternalUsedAmount",
      "EndingInventoryQuantity",
      "EndingInventoryAmount",
      "AdjustedQuantity",
      "AdjustedAmount",
    ];

    const headerRow = [
      formatMessage("product_name"),
      formatMessage("unit_price"),
      formatMessage("carry_over_quantity"),
      formatMessage("carry_over_amount"),
      formatMessage("incoming_quantity"),
      formatMessage("incoming_amount"),
      formatMessage("sales_price"),
      formatMessage("sale_quantity"),
      formatMessage("sale_amount"),
      formatMessage("cost"),
      formatMessage("internal_used_quantity"),
      formatMessage("internal_used_amount"),
      formatMessage("ending_inventory_quantity"),
      formatMessage("ending_inventory_amount"),
      formatMessage("adjusted_quantity"),
      formatMessage("adjusted_amount"),
    ];

    const filteredInventoryList = allInventoryList
      .filter((inventory) => inventory.Id !== 0);
    
    const dataRows = filteredInventoryList.map((item: IInventoryList, filteredListIndex: number) =>
        columns.map((key, index) => {
          const value = (item as Record<string, any>)[key] ?? "";
          // Only wrap ProductName column in quotes, excluding "Total" row
          if (index === 0 && value && filteredListIndex < filteredInventoryList.length - 1) {
            // Escape any existing double quotes and wrap in quotes
            return `"${String(value).replace(/"/g, '""')}"`;
          }
          return value;
        })
      );

    const allRows = [headerRow, ...dataRows];

    const csvRows = allRows.map(row => row.join(','));
    const csv = csvRows.join('\n');
    
    const csvWithBom = "\uFEFF" + csv;
    const blob = new Blob([csvWithBom], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const currentDate = new Date();
    a.download = `${formatMessage("inventory_csv")}-${moment(currentDate).format("YYYYMMDD_HHmmss")}`;
    document.body.appendChild(a);
    a.click();
    toast.success(formatMessage("downloaded_started_successfully"));
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (userType === userTypeEnum[userTypeEnum.Store]) {
      setStoreIdSearch(getStoreIdFromLocalStorage());
    }
    GetAllStoreOptionService();
    return () => {
      resetInventoryFilterDetail();
      setStoreIdSearch(-1);
      resetInventoryList();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    callGetInventoryManagementListService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (inventoryFilter.isFilterChanged) {
        GetAllInventoryListService(
          Number(storeIdSearch),
          sorting.orderBy,
          sorting.column,
          inventoryFilter
        );
        setInventoryFilterDetail({ ...inventoryFilter, isFilterChanged: false });
      }
  }, [inventoryFilter.isFilterChanged]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "language" of the useEffect changed.
   */
  useEffect(() => {
    if (islanguageChanged) {
      callGetInventoryManagementListService();
      updateLanguageChangedFlag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [islanguageChanged]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateGroupstate.success" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateInventorytState.success) {
      resetAddUpdateInventory();
      callGetInventoryManagementListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUpdateInventorytState.success]);

  /**
   * This function shows the add user pop up from where we can add new user and update existing user. we use id to identify
   * that we are adding or updating user.
   * "Id == -1" : we are adding new user
   * "Id != -1" : means we are updating existing user
   */
  const openAddInventoryManagementHandler = (
    row: IInventoryList,
    type: addInventoryTypeEnum
  ) => {
    setSelectedRow(row);
    setSelectedAddInventoryType(type);
    props.addToggleHandler();
  };

  const openInventoryManagementHandler = (row: IInventoryList) => {
    setSelectedId(row.ProductId);
    props.showPopupToggleHandler();
  };

  const columArray: any = [
    {
      columnType: ColumnType.Hidden,
      accessorKey: "Id",
    },
    {
      columnType: ColumnType.JsxElement,
      jsxElementFormatter: (info: CellContext<any, any>) => {
        const row = info.row.original;
        return (
          <>
            {row.Id !== allInventoryList.length && row.Id !== 0 ? (
              <div className="d-flex gap-2">
                {isOperationPermittedRoleBase(
                  ModuleType.InventoryManagement,
                  ActionType.Delete
                ) && (
                  <>
                    <Button
                      variant="dark"
                      size="sm"
                      title={formatMessage("add_incoming_quantity")}
                      onClick={() => {
                        openAddInventoryManagementHandler(
                          row,
                          addInventoryTypeEnum.Incoming
                        );
                      }}
                    >
                      <i className="bi bi-plus-circle"></i>
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      title={formatMessage("add_internal_use_quantity")}
                      onClick={() => {
                        openAddInventoryManagementHandler(
                          row,
                          addInventoryTypeEnum.InternalUse
                        );
                      }}
                    >
                      <i className="bi bi-box-seam"></i>
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      title={formatMessage("add_adjusted_quantity")}
                      onClick={() => {
                        openAddInventoryManagementHandler(
                          row,
                          addInventoryTypeEnum.AdjustedQuantity
                        );
                      }}
                    >
                      <i className="bi bi-pencil-square"></i>
                    </Button>
                  </>
                )}

                <Button
                  variant="primary"
                  size="sm"
                  title={formatMessage("see_details")}
                  onClick={() => {
                    openInventoryManagementHandler(row);
                  }}
                >
                  <i className="bi bi-eye"></i>
                </Button>
              </div>
            ) : (
              ""
            )}
          </>
        );
      },
      accessorKey: "isEdit",
      displayName: "actions",
      headerClassName: "last-column text-center",
    },

    {
      columnType: ColumnType.Sorting,
      accessorKey: "ProductName",
      displayName: "product_name",
      sortingName: "product_name",
    },
    {
      columnType: ColumnType.JsxElement,
      accessorKey: "UnitPriceExcludingTax",
      displayName: "unit_price",
      jsxElementFormatter: (info: CellContext<any, any>) => {
        let cell = info.getValue();
        return cell !== "" ? showAmountWithFormatting(cell) : "";
      },
    },
    {
      columnType: ColumnType.TextHeader,
      accessorKey: "CarryoverQuantity",
      displayName: "carry_over_quantity",
    },
    {
      columnType: ColumnType.JsxElement,
      accessorKey: "CarryoverAmount",
      displayName: "carry_over_amount",
      jsxElementFormatter: (info: CellContext<any, any>) => {
        let cell = info.getValue();
        return cell !== "" ? showAmountWithFormatting(cell) : "";
      },
    },
    {
      columnType: ColumnType.TextHeader,
      accessorKey: "IncomingQuantity",
      displayName: "incoming_quantity",
    },
    {
      columnType: ColumnType.JsxElement,
      accessorKey: "IncomingAmount",
      displayName: "incoming_amount",
      jsxElementFormatter: (info: CellContext<any, any>) => {
        let cell = info.getValue();
        return cell !== "" ? showAmountWithFormatting(cell) : "";
      },
    },
    {
      columnType: ColumnType.JsxElement,
      accessorKey: "PriceExcludingTax",
      displayName: "sales_price",
      jsxElementFormatter: (info: CellContext<any, any>) => {
        let cell = info.getValue();
        return cell !== "" ? showAmountWithFormatting(cell) : "";
      },
    },
    {
      columnType: ColumnType.TextHeader,
      accessorKey: "SalesQuantity",
      displayName: "sale_quantity",
    },
    {
      columnType: ColumnType.JsxElement,
      jsxElementFormatter: (info: CellContext<any, any>) => {
        let cell = info.getValue();
        return cell !== "" ? showAmountWithFormatting(cell) : "";
      },
      accessorKey: "SalesAmount",
      displayName: "sale_amount",
    },
    {
      columnType: ColumnType.JsxElement,
      jsxElementFormatter: (info: CellContext<any, any>) => {
        let cell = info.getValue();
        return cell !== "" ? showAmountWithFormatting(cell) : "";
      },
      accessorKey: "CostAmount",
      displayName: "cost",
    },
    {
      columnType: ColumnType.TextHeader,
      accessorKey: "InternalUsedQuantity",
      displayName: "internal_used_quantity",
    },
    {
      columnType: ColumnType.JsxElement,
      jsxElementFormatter: (info: CellContext<any, any>) => {
        let cell = info.getValue();
        return cell !== "" ? showAmountWithFormatting(cell) : "";
      },
      accessorKey: "InternalUsedAmount",
      displayName: "internal_used_amount",
    },
    {
      columnType: ColumnType.TextHeader,
      accessorKey: "EndingInventoryQuantity",
      displayName: "ending_inventory_quantity",
    },
    {
      columnType: ColumnType.JsxElement,
      jsxElementFormatter: (info: CellContext<any, any>) => {
        let cell = info.getValue();
        return cell !== "" ? showAmountWithFormatting(cell) : "";
      },
      accessorKey: "EndingInventoryAmount",
      displayName: "ending_inventory_amount",
    },
    {
      columnType: ColumnType.TextHeader,
      accessorKey: "AdjustedQuantity",
      displayName: "adjusted_quantity",
    },
    {
      columnType: ColumnType.JsxElement,
      jsxElementFormatter: (info: CellContext<any, any>) => {
        let cell = info.getValue();
        return cell !== "" ? showAmountWithFormatting(cell) : "";
      },
      accessorKey: "AdjustedAmount",
      displayName: "adjusted_amount",
    },
  ];
  const columns = getColumns<IInventoryVM, number>(
    columArray,
    sorting,
    handleSortingChange
  );

  const closeAddInventoryPopupHandler = () => {
    setSelectedAddInventoryType(addInventoryTypeEnum.Incoming);
    props.addToggleHandler();
  };

  return (
    <React.Fragment>
      {props.showAddPopup && (
        <AddInventoryInfo
          storeId={storeIdSearch}
          selectedData={selectedRow}
          addType={selectedAddInventoryType}
          modalClosed={closeAddInventoryPopupHandler}
        />
      )}
      {props.showShowPopup && (
        <AddEditProduct
          id={selectedId}
          modalClosed={props.showPopupToggleHandler}
          viewOnly={true}
        />
      )}
      {(getAllInventoryListState.inProgress ||
        masterManagementStore.inProgress) && (
        <FormLoader
          loading={
            getAllInventoryListState.inProgress ||
            masterManagementStore.inProgress
          }
        />
      )}
      <div className="mt-4">
        <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
          <div className="row align-items-center">
            <div className="col-6">
              <div className="page_title_wrap">
                <h1 className="page_title">
                  <FormattedMessage id="inventory_management" />
                </h1>
                <ol className="breadcrumb">
                  <li className="breadcrumb-item active" aria-current="page">
                    <FormattedMessage id="inventory_management" />
                  </li>
                </ol>
              </div>
            </div>
            <div className="col-6">
              <ul className="add-list">
                <li>
                  <LanguageSlider />
                </li>
                <li>
                  {userType !== userTypeEnum[userTypeEnum.Store] && (
                    <>
                      {storeIdSearch && (
                        <div className="input-group">
                          <div className="input-group-text">
                            <span role="button" onClick={() => {}}>
                              <FontAwesomeIcon icon={faSearch} />
                            </span>
                          </div>
                          <DropdownInput
                            className="form-control"
                            placeholder={formatMessage("search_by_store_name")}
                            value={storeIdSearch}
                            onChange={handleStoreIdSearch}
                            options={getAllStoreOptionsForInventory.map(
                              (option) => ({
                                label: option.value,
                                value: option.id,
                              })
                            )}
                          />
                        </div>
                      )}
                    </>
                  )}
                </li>
              </ul>
            </div>
          </div>
        </div>
        {storeIdSearch > 0 && (
          <div className=" mb-2">
            <Formik
              initialValues={_.omit(inventoryFilter, ["isFilterActive", "isFilterChanged"])}
              enableReinitialize={true}
              validationSchema={invertoryFilterValidateSchema}
              validateOnBlur={true}
              validateOnChange={true}
              onSubmit={async (values) => {
                if(values.IsAll || values.IsSale || values.IsPurchase){
                  let tempValues = {
                    ProductName: values.ProductName?.trim(),
                    IsAll: values.IsAll,
                    IsSale: values.IsSale,
                    IsPurchase: values.IsPurchase,
                    MonthYear: values.MonthYear,
                  };
                  setInventoryFilterDetail({
                    ...tempValues,
                    isFilterActive: true,
                    isFilterChanged: true,
                    showClear: _.isEqual(
                      {
                        ProductName: "",
                        IsAll: false,
                        IsSale: false,
                        IsPurchase: false,
                        MonthYear: getEffectiveMonthYear()
                      },
                      _.omit(values, "showClear")
                    )
                      ? false
                      : true,
                  });
                }
                else{
                  toast.error(formatMessage("please_select_atleast_one_checkbox"));
                }
              }}
            >
              {({ values, setFieldValue }) => {
                // if((values.IsAll || values.IsSale || values.IsPurchase))
                //   setErr(false)
                return (
                  <Form className="user-form p-0 ">
                    <div className="row">
                      {/* MonthYear */}
                      <div className="col-lg-3 col-sm-6 col-12 form-group">
                        <Field
                          name="MonthYear"
                          as={FormikMonthYearFieldMUI}
                          disableFuture={true}
                        />
                      </div>
    
                      {/* ProductName */}
                      <div className="col-lg-3 col-sm-6 col-12 form-group">
                        <Field
                          name="ProductName"
                          type="text"
                          className="form-control"
                          as={FormikFormInput}
                          placeholder="product_name"
                        />
                      </div>
    
                      {/* Checkboxes */}
                      <div className="col-lg-3 col-sm-6 col-12 form-group">
                        <div className="row g-0 pt-2 align-items-center">
                          <div className="col-sm-3">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="is_all"
                              checked={values.IsAll}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFieldValue("IsAll", checked);
                                if (checked) {
                                  setFieldValue("IsSale", false);
                                  setFieldValue("IsPurchase", false);
                                }
                              }}
                            />
                            <label
                              className="form-check-label ms-2"
                              htmlFor="is_all"
                            >
                              <FormattedMessage id="all" />
                            </label>
                          </div>
    
                          <div className="col-sm-4">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="is_sale"
                              checked={values.IsSale}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFieldValue("IsSale", checked);
                                if (checked) {
                                  setFieldValue("IsAll", false);
                                }
                              }}
                            />
                            <label
                              className="form-check-label ms-2"
                              htmlFor="is_sale"
                            >
                              <FormattedMessage id="sale" />
                            </label>
                          </div>
    
                          <div className="col-sm-5">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="is_purchase"
                              checked={values.IsPurchase}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFieldValue("IsPurchase", checked);
                                if (checked) {
                                  setFieldValue("IsAll", false);
                                }
                              }}
                            />
                            <label
                              className="form-check-label ms-2"
                              htmlFor="is_purchase"
                            >
                              <FormattedMessage id="purchase" />
                            </label>
                          </div>
                        </div>
                      </div>
    
                      {/* Buttons */}
                      <div className="col-lg-3 col-sm-6 col-12 text-end">
                        <Button
                          type="submit"
                          disabled={
                            getAllInventoryListState.inProgress ||
                            addUpdateInventorytState.inProgress
                          }
                          className="btn btn-primary"
                        >
                          <FormattedMessage id="apply" />
                        </Button>
                        {inventoryFilter.isFilterActive && inventoryFilter.showClear && (
                          <Button
                            type="button"
                            className="ms-2"
                            variant="btn btn-secondary"
                            onClick={() => {
                              resetInventoryList();
                              setInventoryFilterDetail({
                                ProductName: "",
                                IsAll: false,
                                IsSale: false,
                                IsPurchase: false,
                                MonthYear: getEffectiveMonthYear(),
                                isFilterActive: false,
                                isFilterChanged: true,
                                showClear: false,
                              });
                            }}
                          >
                            <FormattedMessage id="clear" />
                          </Button>
                        )}
                        {userType !== userTypeEnum[userTypeEnum.SuperAdmin] && (
                          <Button
                            type="button"
                            variant="btn btn-primary"
                            className={`ms-2 ${allInventoryList.length === 0 ? "cursor-not-allowed" : ""}`}
                            disabled={allInventoryList.length === 0}
                            onClick={downloadCSVHandler}
                          >
                            <FormattedMessage id="csv_output" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* {
                      err  && 
                      <span data-testid="testError" 
                        className="text-center text-danger errorMessage">
                        <FormattedMessage id="please_select_atleast_one_checkbox" />
                      </span>
                    } */}
                  </Form>
                )}
              }
            </Formik>
          </div>
        )}
        <div className={`${openPanel ? "panelOpen" : ""}`}>
          <div className="card changingPanel">
            <div className="tableDesign row-eq-height tableDevices">
              <div
                className="table-responsive inventoryTable"
                data-testid="table"
              >
                {allInventoryList && (
                  <Grid
                    data={allInventoryList}
                    columns={columns}
                    isInProgress={getAllInventoryListState.inProgress}
                    hiddenColumns={["Id"]}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ShowPopupHOC(AddPopupHOC(observer(InventroyList)));
